import React, { FC, useEffect, useMemo, useState } from 'react';
import styles from './index.module.css';
import { Dropdown, Divider, Button, message, Modal, Input } from 'antd';
import type { MenuProps } from 'antd';
import cx from 'classnames';
import {
  Archive,
  ArrowDown,
  ArrowUp,
  Edit,
  MoreHorizontal,
  Share,
  Trash
} from 'lucide-react';
import { AssetCard } from './AssetCard';
import { deleteItem } from 'appJS/utils/fetch';
import { AssetVersion, DesignAsset } from './types';
import { useDesignAssetsState } from '../../../clients/providers/DesignAssets';
import track from 'appJS/utils/analytics';
import { sortDesignGroupAssets } from 'appJS/views/design_feedback/DesignGroupNavigation/utils';
import VersionSelector from './VersionSelector';
import { useAssetVersioningState } from './AssetVersioningContext';

const versionsFor = (asset: DesignAsset): AssetVersion[] =>
  asset.versions?.length
    ? asset.versions
    : [
        {
          version: 1,
          createdAt: asset.createdAt,
          url: asset.url,
          fileName: asset.name,
          fileType: asset.fileType
        }
      ];

type GroupedAssetProps = {
  name: string;
  groups: string[];
  assets: DesignAsset[];
  disabled: boolean;
  showDropdown: boolean;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
};

export const GroupedAssets: FC<GroupedAssetProps> = ({
  name,
  assets,
  groups,
  disabled,
  showDropdown,
  canMoveUp = false,
  canMoveDown = false,
  onMoveUp,
  onMoveDown
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [deleteGroupModalOpen, setDeleteGroupModalOpen] = useState(false);
  const [deleteGroupSubmitting, setDeleteGroupSubmitting] = useState(false);
  const [renameGroupModalOpen, setRenameGroupModalOpen] = useState(false);
  const [renameGroupName, setRenameGroupName] = useState(name);
  const [renameGroupSubmitting, setRenameGroupSubmitting] = useState(false);
  const [archiveGroupModalOpen, setArchiveGroupModalOpen] = useState(false);
  const [archiveGroupSubmitting, setArchiveGroupSubmitting] = useState(false);

  const {
    onAddToGroup,
    setIsGroupNameModalOpen,
    container,
    setData,
    bugherdUrl,
    onSharingClick,
    isGuest,
    renameGroup,
    updateGroupStatus
  } = useDesignAssetsState();
  const hasAssetVersioningExperiment =
    useAssetVersioningState()?.enabled || false;
  const groupVersions = useMemo(() => {
    if (!hasAssetVersioningExperiment) return [];
    const unique = new Map<number, AssetVersion>();
    assets.forEach(asset => {
      versionsFor(asset).forEach(version => {
        if (!unique.has(version.version)) unique.set(version.version, version);
      });
    });
    return Array.from(unique.values()).sort((a, b) => a.version - b.version);
  }, [assets, hasAssetVersioningExperiment]);
  const [selectedGroupVersion, setSelectedGroupVersion] = useState<
    AssetVersion | 'all' | undefined
  >(hasAssetVersioningExperiment ? 'all' : undefined);

  useEffect(() => {
    if (!hasAssetVersioningExperiment || selectedGroupVersion === 'all') return;
    if (
      !groupVersions.some(
        version => version.version === selectedGroupVersion?.version
      )
    ) {
      setSelectedGroupVersion('all');
    }
  }, [hasAssetVersioningExperiment, groupVersions, selectedGroupVersion]);

  if (assets.length === 0) return null; // don't render empty groups

  const sortedAssets = sortDesignGroupAssets(assets);
  const visibleAssets =
    hasAssetVersioningExperiment &&
    selectedGroupVersion &&
    selectedGroupVersion !== 'all'
      ? sortedAssets.filter(asset =>
          versionsFor(asset).some(
            version => version.version === selectedGroupVersion.version
          )
        )
      : sortedAssets;
  const firstAsset = visibleAssets[0] || sortedAssets[0];
  const showViewAllLink = !!firstAsset?.groupName;
  const viewAllHref = firstAsset
    ? `/projects/${firstAsset.projectId}/assets/${firstAsset.id}${
        hasAssetVersioningExperiment &&
        selectedGroupVersion &&
        selectedGroupVersion !== 'all'
          ? `?asset_version=${selectedGroupVersion.version}`
          : ''
      }`
    : undefined;

  const handleVisibleChange = (visible: boolean) => {
    setIsMenuOpen(visible);
  };

  const runDeleteGroup = async () => {
    const groupAssetIds = sortedAssets.map(({ id }) => id);

    // @ts-expect-error
    setData((previousValue: DesignAsset[]): DesignAsset[] =>
      previousValue.filter(({ id }) => !groupAssetIds.includes(id))
    );

    const results = await Promise.allSettled(
      sortedAssets.map(async asset => {
        const response = await deleteItem(
          `/projects/${asset.projectId}/assets/${asset.id}`
        );

        if (response?.status === 'ok') {
          track('Deleted group');
          return asset.id;
        }

        return null;
      })
    );

    const failedCount = results.filter(result => {
      if (result.status === 'rejected') return true;
      return result.status === 'fulfilled' && result.value === null;
    }).length;

    if (failedCount > 0) {
      message.error(
        'Some assets in this group could not be deleted. Please try again.'
      );
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } else {
      message.success('Deleted asset group');
    }
  };

  const closeDeleteGroupModal = () => {
    setDeleteGroupModalOpen(false);
  };

  const handleDeleteGroupClick = () => {
    setIsMenuOpen(false);
    setDeleteGroupSubmitting(false);
    setDeleteGroupModalOpen(true);
  };

  const handleDeleteGroupConfirm = async () => {
    setDeleteGroupSubmitting(true);
    try {
      await runDeleteGroup();
      closeDeleteGroupModal();
    } finally {
      setDeleteGroupSubmitting(false);
    }
  };

  const handleRenameGroupClick = () => {
    setIsMenuOpen(false);
    setRenameGroupName(name);
    setRenameGroupSubmitting(false);
    setRenameGroupModalOpen(true);
  };

  const closeRenameGroupModal = () => {
    setRenameGroupModalOpen(false);
  };

  const handleRenameGroupConfirm = async () => {
    const nextGroupName = renameGroupName.trim();

    if (!nextGroupName) {
      message.error('Please enter a group name.');
      return;
    }

    if (nextGroupName !== name && groups.includes(nextGroupName)) {
      message.error('A group with this name already exists.');
      return;
    }

    setRenameGroupSubmitting(true);
    try {
      const renamed = await renameGroup(name, nextGroupName);
      if (renamed) closeRenameGroupModal();
    } finally {
      setRenameGroupSubmitting(false);
    }
  };

  const handleArchiveGroupClick = () => {
    setIsMenuOpen(false);
    setArchiveGroupSubmitting(false);
    setArchiveGroupModalOpen(true);
  };

  const closeArchiveGroupModal = () => {
    setArchiveGroupModalOpen(false);
  };

  const handleArchiveGroupConfirm = async () => {
    setArchiveGroupSubmitting(true);
    try {
      const updated = await updateGroupStatus(
        name,
        disabled ? 'created' : 'archived'
      );
      if (updated) closeArchiveGroupModal();
    } finally {
      setArchiveGroupSubmitting(false);
    }
  };

  const menuItems = [
    {
      label: 'Rename group',
      icon: <Edit className={styles.menuIcon} />,
      key: 'rename group',
      onClick: handleRenameGroupClick,
      show: !disabled
    },
    {
      label: disabled ? 'Unarchive group' : 'Archive group',
      icon: <Archive className={styles.archiveIcon} />,
      key: 'archive group',
      onClick: handleArchiveGroupClick
    },
    {
      label: 'Move group up',
      icon: <ArrowUp className={styles.menuIcon} />,
      key: 'move group up',
      onClick: onMoveUp,
      disabled: !canMoveUp
    },
    {
      label: 'Move group down',
      icon: <ArrowDown className={styles.menuIcon} />,
      key: 'move group down',
      onClick: onMoveDown,
      disabled: !canMoveDown
    },
    {
      label: (
        <span className={styles.confirm}>Delete group</span>
      ),
      icon: <Trash className={styles.trashIcon} />,
      key: 'delete group',
      onClick: handleDeleteGroupClick
    }
  ];
  const items: MenuProps['items'] = menuItems
    .filter(({ show }) => show !== false)
    .map(({ show, ...item }) => item);

  const showHeader = groups.length > 0;

  return (
    <>
      <Modal
        title="Rename group"
        open={renameGroupModalOpen}
        closeIcon={false}
        onOk={handleRenameGroupConfirm}
        onCancel={closeRenameGroupModal}
        confirmLoading={renameGroupSubmitting}
        getContainer={container}
        wrapClassName={styles.groupNameModal}
        okText="Save"
      >
        <Input
          value={renameGroupName}
          onChange={({ target: { value } }) => setRenameGroupName(value)}
          onPressEnter={handleRenameGroupConfirm}
          placeholder="Enter group name here"
        />
      </Modal>
      <Modal
        title={disabled ? 'Unarchive group?' : 'Archive group?'}
        open={archiveGroupModalOpen}
        closeIcon={false}
        onCancel={closeArchiveGroupModal}
        getContainer={container}
        wrapClassName={styles.groupNameModal}
        footer={
          <div className={styles.deleteGroupModalFooter}>
            <Button key="cancel" onClick={closeArchiveGroupModal}>
              Cancel
            </Button>
            <Button
              key="archive"
              type="primary"
              loading={archiveGroupSubmitting}
              onClick={handleArchiveGroupConfirm}
            >
              {disabled ? 'Unarchive' : 'Archive'}
            </Button>
          </div>
        }
      >
        <p className={styles.deleteGroupModalBody}>
          {disabled
            ? `This will restore ${assets.length} design${
                assets.length === 1 ? '' : 's'
              } in "${name}".`
            : `This will move ${assets.length} design${
                assets.length === 1 ? '' : 's'
              } in "${name}" to archived designs.`}
        </p>
      </Modal>
      <Modal
        title="Delete group?"
        open={deleteGroupModalOpen}
        closeIcon={false}
        onCancel={closeDeleteGroupModal}
        getContainer={container}
        wrapClassName={cx(styles.groupNameModal, styles.deleteGroupModal)}
        footer={
          <div className={styles.deleteGroupModalFooter}>
            <Button key="cancel" onClick={closeDeleteGroupModal}>
              Cancel
            </Button>
            <Button
              key="delete"
              type="primary"
              loading={deleteGroupSubmitting}
              onClick={handleDeleteGroupConfirm}
              className={styles.deleteGroupModalDeleteBtn}
            >
              Delete
            </Button>
          </div>
        }
      >
        <p className={styles.deleteGroupModalBody}>
          {`This will permanently delete ${sortedAssets.length} design${
            sortedAssets.length === 1 ? '' : 's'
          } in "${name}".`}
        </p>
      </Modal>
      {showHeader && (
        <>
          <div className={styles.groupHeader}>
            <h2 className={styles.groupHeaderTitle}>{name}</h2>
            {(showViewAllLink || showDropdown) && (
              <div className={styles.groupMenu}>
                {hasAssetVersioningExperiment &&
                  showViewAllLink &&
                  selectedGroupVersion && (
                    <VersionSelector
                      versions={groupVersions}
                      selected={selectedGroupVersion}
                      onSelect={setSelectedGroupVersion}
                      onSelectAll={() => setSelectedGroupVersion('all')}
                      showAllOption
                      container={container}
                    />
                  )}
                {showViewAllLink && viewAllHref && (
                  <a
                    className={styles.groupHeaderViewAll}
                    href={viewAllHref}
                  >
                    View all
                  </a>
                )}
                {showDropdown && (
                  <>
                    <Dropdown
                      className={styles.assetDropdown}
                      getPopupContainer={() => container}
                      trigger={['click']}
                      placement="bottomRight"
                      arrow={{ pointAtCenter: true }}
                      overlayClassName={styles.groupOverlay}
                      onOpenChange={handleVisibleChange}
                      menu={{ items }}
                      open={isMenuOpen}
                      key={name}
                    >
                      <MoreHorizontal
                        onClick={event => event.preventDefault()}
                        className={cx(styles.moreIcon, {
                          [styles.active]: isMenuOpen
                        })}
                      />
                    </Dropdown>
                    {!disabled && (
                      <Button
                        icon={<Share className={styles.shareGroupIcon} />}
                        type="link"
                        onClick={() => onSharingClick({ assetGroupName: name })}
                      />
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <Divider key={'divider'} />
        </>
      )}
      {visibleAssets.map((asset, index) => (
        <AssetCard
          asset={asset}
          key={asset.id}
          container={container}
          canManageAssets
          disabled={disabled}
          setData={setData}
          bugherdUrl={bugherdUrl}
          existingGroups={groups}
          handleSharingClick={() => onSharingClick({ assetId: asset.id })}
          onAddToGroup={onAddToGroup}
          openNewGroupModal={() => setIsGroupNameModalOpen(asset.id)}
          isGuest={isGuest}
          index={index}
          preferredVersion={
            hasAssetVersioningExperiment && selectedGroupVersion !== 'all'
              ? selectedGroupVersion?.version
              : undefined
          }
        />
      ))}
    </>
  );
};
